import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { parseModelResponse } from '../utils/parseModelResponse';
import { getColumnColor, getCellColor } from '../utils/colorUtils';
import { buildPayload } from '../utils/payloadBuilder';
import ResponseTableView from './ResponseTableView';

try { localStorage.setItem('RTV_DEBUG', '1'); } catch {}

try {
  localStorage.setItem('RTC_DEBUG', '1');
  localStorage.setItem('RTV_DEBUG', '1');
} catch {}

const RTC_DEBUG = (() => { try { return localStorage.getItem('RTC_DEBUG') === '1' || localStorage.getItem('RTV_DEBUG') === '1'; } catch { return false; } })();
const dlog  = (...a) => { if (RTC_DEBUG) console.debug(...a); };
const dwarn = (...a) => { if (RTC_DEBUG) console.warn(...a); };
const dgroup = (label) => { if (RTC_DEBUG && console.groupCollapsed) console.groupCollapsed(label); };
const dgroupEnd = () => { if (RTC_DEBUG && console.groupEnd) console.groupEnd(); };
const preview = (v, n = 300) => {
  try { return typeof v === 'string' ? v.slice(0, n) : JSON.stringify(v).slice(0, n); }
  catch { return String(v).slice(0, n); }
};

const ResponseTableContainer = ({
  analysisId, responses, providersLabels, selectedProviders, providerColumns,
  patnr_id, time, responseTimes, activeTimers, selectedText, onSelectText, info, existingReview
}) => {
  const maxSteps = responses.reduce((max, r, idx) => {
    let steps = 0;
    try {
      const parsed = parseModelResponse(r);
      steps = parsed?.treatment_steps?.length || 0;
      dlog('[RTC][maxSteps] col=', idx, 'label=', providersLabels?.[idx], 'steps=', steps, {
        title: parsed?.title || '',
        descLen: (parsed?.description || '').length,
        username: parsed?.username ?? null
      });
    } catch (e) {
      dwarn('[RTC][maxSteps] parse error at col', idx, e);
    }
    return Math.max(max, steps);
  }, 0);
  dlog('[RTC] computed maxSteps =', maxSteps);

  const rowLabels = [
    'Title',
    ...Array.from({ length: maxSteps }, (_, i) => `Step ${i + 1}`),
    'Description',
    'Send'
  ];

  useEffect(() => {
    if (!existingReview || !Array.isArray(existingReview.comments)) return;
    if (lastFilledIdRef.current === analysisId) return;
    if (!analysisId) return;
    const COL_INDEX = 0;
    const nextCellStates = {};
    const nextComments  = {};
    rowLabels.forEach((label, rowIndex) => {
      if (label === 'Send') return;
      const match = existingReview.comments.find(
        c => c.Section === label
      );
      if (!match) return;
      if (match.Approved === true) {
        nextCellStates[`${rowIndex}_${COL_INDEX}`] = 'ok';
      } else if (match.Approved === false) {
        nextCellStates[`${rowIndex}_${COL_INDEX}`] = 'bad';
      }
      if (match.DoctorsComment && match.DoctorsComment.trim() !== '') {
        nextComments[`${rowIndex}_${COL_INDEX}`] = match.DoctorsComment.trim();
      }
    });
    setCellStates(nextCellStates);
    setComments(nextComments);
    console.log('[RTC] prefilled from existingReview', {
      nextCellStates,
      nextComments,
      reviewBy: existingReview.assistant,
      reviewCreatedAt: existingReview.created_at,
      analysisId
    });
    lastFilledIdRef.current = analysisId;
  }, [existingReview, rowLabels, analysisId]);

  useEffect(() => {
    dlog('[RTC] rowLabels snapshot:', rowLabels, {
      total: rowLabels.length,
      descRowIndex: rowLabels.length - 2
    });
  }, [maxSteps]);

  useEffect(() => {
    dlog('[RTC] selectedText in container:', JSON.stringify(selectedText));
  }, [selectedText]);

  const [cellStates, setCellStates] = useState({});
  const [comments, setComments] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeCellKey, setActiveCellKey] = useState(null);
  const [tempComment, setTempComment] = useState('');
  const [sending, setSending] = useState({});
  const [sentSuccess, setSentSuccess] = useState({});
  const [username, setUsername] = useState({});
  const [userRole, setUserRole] = useState({});
  const cellRefs = useRef({});
  const metaInfo = { patnr_id, time };

  useEffect(() => {
    if (!RTC_DEBUG) return;
    responses.forEach((r, i) => {
      let parsed;
      try { parsed = parseModelResponse(r); } catch (e) { dwarn('[RTC] parse error at col', i, e); }
      dlog(`col=${i} label="${providersLabels?.[i]}"`, {
        rawPreview: preview(r),
        title: parsed?.title ?? '',
        descLen: (parsed?.description || '').length,
        stepsLen: parsed?.treatment_steps?.length ?? 0,
        username: parsed?.username ?? null
      });
    });
    dgroupEnd();
  }, [responses, providersLabels]);

  useEffect(() => {
    if (!RTC_DEBUG) return;
    const totalStates = Object.keys(cellStates).length;
    const totalComments = Object.keys(comments).length;
    dlog('[RTC] cellStates/count =', totalStates, cellStates);
    dlog('[RTC] comments/count =', totalComments, comments);
  }, [cellStates, comments]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { dlog('[RTC] no token -> skip get_username'); return; }

    axios.get(`${process.env.REACT_APP_API_URL}/get_username`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      dlog('[RTC] /get_username response:', res?.data);
      if (res.data?.username) {
        setUsername(res.data.username);
        setUserRole(res.data.role);
      }
    })
    .catch(err => {
      console.error('Failed to load username', err);
    });
  }, []);

  const lastFilledIdRef = React.useRef(null);

  useEffect(() => {
    setCellStates({});
    setComments({});
    setAnchorEl(null);
    setActiveCellKey(null);
    setTempComment('');
    setSending({});
    setSentSuccess({});
    lastFilledIdRef.current = null;

  }, [analysisId]);

  const handleMark = (row, col, type, event = null) => {
    const key = `${row}_${col}`;
    dlog('[RTC][handleMark]', { row, col, type, key });
    setCellStates(prev => ({ ...prev, [key]: type }));

    if (type === 'bad' && event) {
      const parsed = parseModelResponse(responses[col]);
      let existingComment = comments[key] || '';

      if (!existingComment && parsed) {
        if (row === 0) {
          existingComment = parsed.title_comment || '';
        } else if (row === rowLabels.length - 2) {
          existingComment = parsed.description_comment || '';
        } else {
          const stepIndex = row - 1;
          const step = parsed.treatment_steps?.[stepIndex];
          if (!step) {
            dwarn('[RTC][handleMark] step not found for', { row, col, stepIndex, stepsLen: parsed.treatment_steps?.length ?? 0 });
          }
          existingComment = step?.doctorsComment || '';
        }
      }

      dlog('[RTC][handleMark] existingComment=', existingComment);
      setAnchorEl(cellRefs.current[key]);
      setActiveCellKey(key);
      setTempComment(existingComment);
    }
  };

  const handleSaveComment = () => {
    dlog('[RTC][handleSaveComment] key=', activeCellKey, 'text=', tempComment);
    if (activeCellKey && tempComment.trim()) {
      setComments(prev => ({ ...prev, [activeCellKey]: tempComment.trim() }));
      setAnchorEl(null);
      setTempComment('');
    }
  };

  const isColumnComplete = (colIndex) => {
    const total = rowLabels.length - 1;
    let count = 0;
    for (let i = 0; i < total; i++) {
      const key = `${i}_${colIndex}`;
      const state = cellStates[key];
      if (state === 'ok' || state === 'bad') count++;
    }
    const complete = count === total;
    dlog('[RTC][isColumnComplete]', { colIndex, count, total, complete });
    return complete;
  };

  const onSendClick = async (colIndex) => {
    dlog('[RTC][onSendClick] colIndex=', colIndex);
    setSending(prev => ({ ...prev, [colIndex]: true }));
    setSentSuccess(prev => ({ ...prev, [colIndex]: false }));

    const parsed = parseModelResponse(responses[colIndex]);
    if (!parsed) {
      setSending(prev => ({ ...prev, [colIndex]: false }));
      return;
    }

    const provider = providerColumns[colIndex];

    const payload = buildPayload({
      parsed,
      rowLabels,
      cellStates,
      comments,
      colIndex,
      providersLabels,
      providerColumns,
      metaInfo: { patnr_id, time, username },
      info
    });
    dlog('buildPayload =', buildPayload);

    try {
      console.log('[DEBUG] payload =', payload);
      console.log('[DEBUG] Array.isArray(payload.comments) =', Array.isArray(payload.comments));
      const changedRows = payload.comments.filter((item, index) => {
        const key = `${index}_${colIndex}`;
        return cellStates[key] || comments[key];  // manually marked or commented
      });
      const missingComments = changedRows.filter(row => row.Approved === false && !row.DoctorsComment?.trim());
      //dgroup('[RTC][onSendClick] payload summary');
      dlog('rows total =', payload.comments.length);
      dlog('changedRows =', changedRows.length, changedRows);
      if (missingComments.length > 0) {
        dwarn('Some "bad" rows are missing comments:', missingComments);
      }
      dgroupEnd();
      console.log('[DEBUG] payload just before sending =', payload);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/llm-analysis-result/column-json`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSentSuccess(prev => ({ ...prev, [colIndex]: true }));
      dlog('[RTC][onSendClick] send OK');
    } catch (err) {
      console.error('[RTC][onSendClick] send failed', err);
    } finally {
      setSending(prev => ({ ...prev, [colIndex]: false }));
    }
  };

  return (
    <ResponseTableView
      responses={responses}
      providersLabels={providersLabels}
      selectedProviders={selectedProviders}
      providerColumns={['comparison_result']}
      rowLabels={rowLabels}
      cellStates={cellStates}
      comments={comments}
      anchorEl={anchorEl}
      tempComment={tempComment}
      sending={sending}
      sentSuccess={sentSuccess}
      cellRefs={cellRefs}
      onMark={handleMark}
      onSaveComment={handleSaveComment}
      onSendClick={onSendClick}
      setTempComment={setTempComment}
      getCellColor={(row, col) => getCellColor(cellStates[`${row}_${col}`])}
      getColumnColor={(col) => getColumnColor(col, rowLabels, cellStates)}
      isColumnComplete={isColumnComplete}
      activeTimers={activeTimers}
      setAnchorEl={setAnchorEl}
      userRole={userRole}
      selectedText={selectedText}
      onSelectText={onSelectText}
    />
  );
};

export default ResponseTableContainer;